import { Tooltip } from "@mantine/core";
import type { IconProps } from "@tabler/icons-react";
import { motion } from "framer-motion";
import { forwardRef, type ComponentType, type MouseEventHandler } from "react";

interface Props {
  icon: ComponentType<IconProps>;
  onClick?: MouseEventHandler<HTMLButtonElement>;
  disabled?: boolean;
  ariaLabel: string;
  /** "ghost" = translucent white (on the gradient header); "solid" = gradient glow. */
  variant?: "ghost" | "solid";
  /** Optional hover hint shown as an elegant tooltip. */
  tooltip?: string;
  /** Merged in (not overwritten) so Mantine Menu/Popover targets keep our styles. */
  className?: string;
}

/**
 * The assistant's own icon button — matches the client-branded AI vibe instead of the
 * platform's standard ActionButton. Springy hover/tap feedback. Forwards its
 * ref (and merges injected props) so it can be a Mantine `Menu.Target`.
 */
export const GlowIconButton = forwardRef<HTMLButtonElement, Props>(
  (
    {
      icon: Icon,
      onClick,
      disabled = false,
      ariaLabel,
      variant = "ghost",
      tooltip,
      className,
      ...rest
    },
    ref
  ) => {
    const base =
      "flex items-center justify-center rounded-full transition-shadow disabled:cursor-not-allowed";
    const styles =
      variant === "solid"
        ? "h-11 w-11 bg-gradient-to-br from-[var(--assistant-from)] to-[var(--assistant-to)] text-white shadow-[0_4px_16px_-2px_var(--assistant-glow)] hover:shadow-[0_6px_22px_-2px_var(--assistant-glow)] disabled:opacity-40 disabled:grayscale"
        : "h-8 w-8 text-white/90 hover:bg-white/20 disabled:opacity-40";

    const button = (
      <motion.button
        {...rest}
        ref={ref}
        type="button"
        onClick={onClick}
        disabled={disabled}
        aria-label={ariaLabel}
        whileHover={disabled ? undefined : { scale: 1.1 }}
        whileTap={disabled ? undefined : { scale: 0.9 }}
        transition={{ type: "spring", stiffness: 500, damping: 24 }}
        className={`${base} ${styles} ${className ?? ""}`}
      >
        <Icon size={variant === "solid" ? 20 : 18} stroke={2.4} />
      </motion.button>
    );

    if (!tooltip) {
      return button;
    }

    return (
      <Tooltip
        label={tooltip}
        withArrow
        openDelay={300}
        fz="xs"
        radius="md"
        position="bottom"
        zIndex={500}
      >
        {button}
      </Tooltip>
    );
  }
);

GlowIconButton.displayName = "GlowIconButton";
